import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldCheck, KeyRound, ArrowRight, Loader2, Check, Eye, EyeOff } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import SEO from '../components/SEO';
import { useT } from '../i18n';
import { clearAuthCache, fetchMe } from '../lib/auth';
import { googleLoginRedirect, consumeGoogleSession, hasGoogleSessionHash, GoogleIcon } from '../lib/googleAuth';
import { AuthShell } from '../components/AuthShell';

/* One door. Authentication identifies the person — Kitxens decides the workspaces. */

const API = process.env.REACT_APP_BACKEND_URL;

function fmtErr(detail, fallback) {
  if (detail == null) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === 'string' ? e.msg : '')).filter(Boolean).join(' ');
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
}

function HumanCheck({ verified, verifying, onVerify, copy }) {
  return (
    <button type="button" onClick={() => !verified && !verifying && onVerify()} data-testid="login-human-check"
      className={`mt-4 w-full flex items-center gap-3 rounded-xl border px-4 h-12 text-left transition-colors ${verified ? 'border-emerald-400 bg-emerald-50' : 'border-neutral-300 bg-white hover:border-neutral-400'}`}>
      <span className={`w-5 h-5 rounded-md border flex items-center justify-center transition-colors ${verified ? 'border-emerald-400 bg-emerald-400' : 'border-neutral-300'}`}>
        {verifying ? <Loader2 className="w-3.5 h-3.5 text-neutral-400 animate-spin" /> : verified ? <Check className="w-3.5 h-3.5 text-white" /> : null}
      </span>
      <span className={`text-[13px] font-medium ${verified ? 'text-emerald-700' : 'text-neutral-600'}`}>
        {verified ? copy.humanOk : verifying ? copy.humanChecking : copy.human}
      </span>
      <ShieldCheck className={`ml-auto w-4 h-4 ${verified ? 'text-emerald-500' : 'text-neutral-400'}`} />
    </button>
  );
}

function SecretInput({ value, onChange, placeholder, testId, show, onToggle, autoFocus = false }) {
  return (
    <div className="relative">
      <input type={show ? 'text' : 'password'} value={value} onChange={onChange} placeholder={placeholder} autoFocus={autoFocus}
        className="mt-1.5 w-full h-12 rounded-xl bg-white border border-neutral-300 pl-4 pr-12 text-sm text-neutral-900 placeholder:text-neutral-400 outline-none focus:border-[#F77F11] transition-colors"
        data-testid={testId} />
      <button type="button" onClick={onToggle} tabIndex={-1} data-testid={`${testId}-toggle`}
        className="absolute right-3 top-1/2 mt-[3px] -translate-y-1/2 w-8 h-8 flex items-center justify-center rounded-lg text-neutral-400 hover:text-neutral-800 transition-colors">
        {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
      </button>
    </div>
  );
}

function RequestInvitation({ isEs }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: '', email: '', restaurant: '' });
  const [state, setState] = useState('idle');
  const submit = async (e) => {
    e.preventDefault();
    setState('busy');
    try {
      const r = await fetch(`${API}/api/os/request-access`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(f) });
      setState(r.ok ? 'sent' : 'idle');
    } catch { setState('idle'); }
  };
  if (state === 'sent') {
    return (
      <p className="mt-5 text-center text-[13px] text-emerald-700 font-semibold" data-testid="login-request-sent">
        ✓ {isEs ? 'Recibido. El equipo Kitxens te contactará muy pronto.' : 'Received. The Kitxens team will be in touch very soon.'}
      </p>
    );
  }
  return (
    <div className="mt-5 text-center">
      {!open ? (
        <button type="button" onClick={() => setOpen(true)} data-testid="login-request-access-btn"
          className="text-[13px] text-neutral-400 hover:text-neutral-800 transition-colors">
          {isEs ? '¿Necesitas acceso?' : 'Need access?'} <span className="font-semibold text-[#F77F11]">{isEs ? 'Solicita una invitación.' : 'Request an invitation.'}</span>
        </button>
      ) : (
        <form onSubmit={submit} className="mt-2 rounded-xl border border-neutral-200 bg-neutral-50 p-4 text-left" data-testid="login-request-form">
          <p className="text-[12px] font-bold text-neutral-700 mb-2">{isEs ? 'Cuéntanos quién eres — nosotros hacemos el resto.' : 'Tell us who you are — we do the rest.'}</p>
          <div className="space-y-2">
            <input required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder={isEs ? 'Tu nombre' : 'Your name'}
              className="w-full h-10 rounded-lg border border-neutral-300 px-3 text-[13px]" data-testid="login-request-name" />
            <input required type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} placeholder="you@email.com"
              className="w-full h-10 rounded-lg border border-neutral-300 px-3 text-[13px]" data-testid="login-request-email" />
            <input value={f.restaurant} onChange={(e) => setF({ ...f, restaurant: e.target.value })} placeholder={isEs ? 'Tu restaurante (opcional)' : 'Your restaurant (optional)'}
              className="w-full h-10 rounded-lg border border-neutral-300 px-3 text-[13px]" data-testid="login-request-restaurant" />
          </div>
          <div className="mt-3 flex items-center gap-3">
            <button disabled={state === 'busy'} data-testid="login-request-submit"
              className="h-10 px-5 rounded-full bg-neutral-900 hover:bg-[#F77F11] text-white text-[12.5px] font-bold transition-colors disabled:opacity-50">
              {state === 'busy' ? '…' : (isEs ? 'Solicitar invitación' : 'Request invitation')}
            </button>
            <button type="button" onClick={() => setOpen(false)} className="text-[12px] text-neutral-400 hover:text-neutral-700">{isEs ? 'Cerrar' : 'Close'}</button>
          </div>
        </form>
      )}
    </div>
  );
}

export default function LoginPage() {
  const navigate = useNavigate();
  const { lang } = useT();
  const isEs = lang === 'es';
  const next = new URLSearchParams(window.location.search).get('next') || '/admin/mission-control';
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState(() => { try { return localStorage.getItem('kx_login_email') || ''; } catch { return ''; } });
  const [password, setPassword] = useState('');
  const [keyword, setKeyword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [showKw, setShowKw] = useState(false);
  const [verified, setVerified] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [busy, setBusy] = useState(false);
  const [googleBusy, setGoogleBusy] = useState(false);
  const [err, setErr] = useState('');
  const [recover, setRecover] = useState('idle');
  const magicToken = new URLSearchParams(window.location.search).get('magic') || '';
  const [magicState, setMagicState] = useState(magicToken ? 'busy' : 'none');

  const copy = isEs
    ? {
        welcome: 'Bienvenido de vuelta.', sub: 'One Login. Infinite Workspaces.',
        email: 'Email', password: 'Contraseña',
        human: 'No soy un robot', humanChecking: 'Verificando…', humanOk: 'Verificado — eres humano',
        continue: 'Continuar', or: 'o', google: 'Continuar con Google', googleChecking: 'Verificando con Google…',
        kwTitle: 'Palabra de seguridad', kwSub: 'Kitxens Admin requiere tu palabra clave — es una PALABRA, no un número.',
        submit: 'Entrar', submitting: 'Verificando…', back: '← Volver',
        errFields: 'Introduce tu email y contraseña.', errHuman: 'Completa la verificación humana.',
        errKeyword: 'Introduce tu palabra clave de seguridad.', errConn: 'Error de conexión — inténtalo de nuevo.',
        errGeneric: 'Algo salió mal. Inténtalo de nuevo.',
        errBadCreds: 'Email o contraseña incorrectos. ¿La olvidaste? Usa el enlace de abajo.',
        errGoogle: 'No encontramos una cuenta Kitxens asociada a ese email de Google.',
        forgot: '¿Olvidaste tu contraseña? Escribe tu email arriba y pulsa aquí', sending: 'Enviando…',
        rescue: '¿Sigues sin poder entrar? Escríbenos a', 
      }
    : {
        welcome: 'Welcome back.', sub: 'One Login. Infinite Workspaces.',
        email: 'Email', password: 'Password',
        human: "I'm not a robot", humanChecking: 'Verifying…', humanOk: 'Verified — you are human',
        continue: 'Continue', or: 'or', google: 'Continue with Google', googleChecking: 'Verifying with Google…',
        kwTitle: 'Security keyword', kwSub: 'Kitxens Admin requires your keyword — it\u2019s a WORD, not a number.',
        submit: 'Sign in', submitting: 'Verifying…', back: '← Back',
        errFields: 'Enter your email and password.', errHuman: 'Complete the human verification.',
        errKeyword: 'Enter your security keyword.', errConn: 'Connection error — please try again.',
        errGeneric: 'Something went wrong. Please try again.',
        errBadCreds: 'Wrong email or password. Forgot it? Use the link below.',
        errGoogle: "We couldn't find a Kitxens account linked to that Google email.",
        forgot: 'Forgot your password? Type your email above and click here', sending: 'Sending…',
        rescue: 'Still locked out? Email us at', 
      };

  useEffect(() => {
    if (!magicToken) return;
    fetch(`${API}/api/auth/magic`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ token: magicToken }) })
      .then(async (r) => {
        if (r.ok) { clearAuthCache(); navigate('/admin/mission-control', { replace: true }); }
        else { setMagicState('error'); }
      }).catch(() => setMagicState('error'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (hasGoogleSessionHash()) {
      setGoogleBusy(true);
      consumeGoogleSession('auto')
        .then((d) => {
          if (!d) return;
          clearAuthCache();
          if (d.context === 'admin') { try { localStorage.setItem('kx_internal', '1'); } catch { /* noop */ } navigate(next, { replace: true }); }
          else if (d.context === 'partner') navigate('/affiliates/dashboard', { replace: true });
          else navigate('/account', { replace: true });
        })
        .catch(() => { setErr(copy.errGoogle); setGoogleBusy(false); });
      return;
    }
    // Only a full admin session may auto-skip the login — any other role would bounce
    // against AdminGate and create a redirect loop.
    fetchMe().then((u) => { if (u && u.role === 'admin') navigate(next, { replace: true }); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, next]);

  const verifyHuman = () => { setVerifying(true); setTimeout(() => { setVerifying(false); setVerified(true); }, 700); };

  const requestRecover = async () => {
    if (!email.trim()) { setErr(copy.errFields); return; }
    setRecover('busy');
    try {
      await fetch(`${API}/api/auth/reset-request`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email: email.trim() }) });
      setRecover('sent'); setErr('');
    } catch { setRecover('idle'); setErr(copy.errConn); }
  };

  const enterWs = async (wid, kw = '') => {
    const r = await fetch(`${API}/api/os/enter`, { method: 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ workspace_id: wid, keyword: kw }) });
    const j = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(typeof j.detail === 'string' ? j.detail : 'error');
    clearAuthCache();
    window.location.href = j.path;
  };

  const submitLogin = async (e) => {
    e.preventDefault();
    setErr('');
    if (!email.trim() || !password) { setErr(copy.errFields); return; }
    if (!verified) { setErr(copy.errHuman); return; }
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/os/login`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), password, human: verified }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (res.status === 401) setErr(copy.errBadCreds);
        else setErr(fmtErr(data?.detail, copy.errGeneric));
        setBusy(false); return;
      }
      try { localStorage.setItem('kx_login_email', email.trim()); } catch { /* noop */ }
      const ws = data.workspaces || [];
      if (ws.some((w) => w.id === 'admin')) { try { localStorage.setItem('kx_internal', '1'); } catch { /* noop */ } }
      const prefs = data.prefs || {};
      if (ws.length === 1 && ws[0].id === 'admin') { setBusy(false); setStep(2); return; }
      const ids = ws.map((w) => w.id);
      // Admins with multiple workspaces ALWAYS land on the Hub to choose where to work.
      if (ids.includes('admin') && ws.length > 1) { navigate('/hub', { replace: true }); return; }
      let target = null;
      if (prefs.startup === 'hub') target = 'hub';
      else if (prefs.startup === 'last' && ids.includes(prefs.last_workspace)) target = prefs.last_workspace;
      else if (ids.includes(prefs.startup)) target = prefs.startup;
      else if (ws.length === 1) target = ws[0].id;
      else target = 'hub';
      if (target === 'hub' || target === 'admin') { navigate('/hub', { replace: true }); return; }
      await enterWs(target);
    } catch { setErr(copy.errConn); setBusy(false); }
  };

  const submitKeyword = async (e) => {
    e.preventDefault();
    setErr('');
    if (!keyword.trim()) { setErr(copy.errKeyword); return; }
    setBusy(true);
    try { await enterWs('admin', keyword.trim()); }
    catch (ex) { setErr(fmtErr(ex.message, copy.errGeneric)); setBusy(false); }
  };

  return (
    <AuthShell testId="login-page" image="/auth/team.png" eyebrow="Kitxens OS"
      title={isEs ? 'Una identidad. Todos tus espacios.' : 'One identity. All your workspaces.'}
      sub={isEs ? 'Entra una vez. Kitxens decide qué workspaces son tuyos — tú solo eliges dónde trabajar hoy.' : 'Sign in once. Kitxens decides which workspaces are yours — you just choose where to work today.'}
      links={[]}>
      <SEO path="/login" title="Login | Kitxens" noindex />
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="relative w-full max-w-md">
        <div className="rounded-2xl border border-neutral-200 bg-white p-8 shadow-[0_10px_40px_-18px_rgba(0,0,0,0.18)]">
          {magicState === 'busy' && (
            <div className="mb-4 rounded-xl bg-orange-50 border border-[#F77F11]/30 px-4 py-3 text-[12.5px] font-semibold text-[#d8651a] inline-flex items-center gap-2 w-full" data-testid="login-magic-status">
              <Loader2 className="w-4 h-4 animate-spin" /> {isEs ? 'Verificando tu enlace de acceso…' : 'Verifying your access link…'}
            </div>
          )}
          {magicState === 'error' && (
            <div className="mb-4 rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-[12.5px] font-semibold text-red-600 w-full" data-testid="login-magic-error">
              {isEs ? 'Ese enlace ya no es válido. Pide uno nuevo abajo.' : 'That link is no longer valid. Request a new one below.'}
            </div>
          )}
          {recover === 'sent' && (
            <div className="mb-4 rounded-xl bg-emerald-50 border border-emerald-200 px-4 py-3 text-[12.5px] font-semibold text-emerald-700 w-full" data-testid="login-recover-sent">
              {isEs ? '📬 Si esa cuenta existe, te enviamos un email para crear una nueva contraseña (30 min). Revisa tu bandeja y spam.' : '📬 If that account exists, we emailed you a link to set a new password (30 min). Check your inbox and spam.'}
            </div>
          )}
          <img src="/kitxens-logo.png" alt="Kitxens" className="h-9 w-auto" data-testid="login-kitxens-logo" />
          <h1 className="mt-4 text-2xl font-bold text-neutral-900">{step === 1 ? copy.welcome : copy.kwTitle}</h1>
          <p className="mt-1 text-[14px] text-neutral-500">{step === 1 ? copy.sub : copy.kwSub}</p>

          <AnimatePresence mode="wait">
            {step === 1 ? (
              <motion.form key="s1" initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -12 }} transition={{ duration: 0.2 }} onSubmit={submitLogin} className="mt-6">
                <label className="text-[11px] font-semibold tracking-wide uppercase text-neutral-500">{copy.email}</label>
                <input type="email" value={email} onChange={(e) => { setEmail(e.target.value); setErr(''); }} placeholder={isEs ? 'tu@email.com' : 'you@email.com'} autoFocus
                  className="mt-1.5 w-full h-12 rounded-xl bg-white border border-neutral-300 px-4 text-sm text-neutral-900 placeholder:text-neutral-400 outline-none focus:border-[#F77F11] transition-colors" data-testid="login-email-input" />
                <label className="mt-4 block text-[11px] font-semibold tracking-wide uppercase text-neutral-500">{copy.password}</label>
                <SecretInput value={password} onChange={(e) => { setPassword(e.target.value); setErr(''); }} placeholder="••••••••••••"
                  testId="login-password-input" show={showPw} onToggle={() => setShowPw((v) => !v)} />
                <HumanCheck verified={verified} verifying={verifying} onVerify={verifyHuman} copy={copy} />
                {err && (
                  <div className="mt-3" data-testid="login-error-box">
                    <p className="text-[12.5px] text-red-500" data-testid="login-error">{err}</p>
                    <p className="mt-1 text-[11.5px] text-neutral-400">{copy.rescue} <a href="mailto:support@kitxens.com" className="font-semibold text-[#F77F11] hover:underline" data-testid="login-support-link">support@kitxens.com</a></p>
                  </div>
                )}
                <button type="submit" disabled={busy} className="mt-5 w-full h-12 rounded-xl bg-[#F77F11] hover:bg-[#d8651a] disabled:opacity-60 text-white font-semibold text-sm transition-colors inline-flex items-center justify-center gap-2" data-testid="login-continue-btn">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null} {copy.continue} {!busy && <ArrowRight className="w-4 h-4" />}
                </button>
                <div className="mt-5 flex items-center gap-3" aria-hidden>
                  <span className="h-px flex-1 bg-neutral-200" />
                  <span className="text-[11px] text-neutral-400 uppercase">{copy.or}</span>
                  <span className="h-px flex-1 bg-neutral-200" />
                </div>
                <button type="button" onClick={() => googleLoginRedirect('/login')} disabled={googleBusy}
                  className="mt-4 w-full h-12 rounded-xl border border-neutral-300 bg-white hover:bg-neutral-50 disabled:opacity-60 text-neutral-800 font-semibold text-sm transition-colors inline-flex items-center justify-center gap-2.5"
                  data-testid="login-google-btn">
                  {googleBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <GoogleIcon />} {googleBusy ? copy.googleChecking : copy.google}
                </button>
                <div className="mt-4 text-center">
                  <button type="button" onClick={requestRecover} disabled={recover === 'busy'}
                    className="text-[12px] font-semibold text-neutral-400 hover:text-[#F77F11] transition-colors disabled:opacity-50" data-testid="login-forgot-btn-step1">
                    {recover === 'busy' ? copy.sending : copy.forgot}
                  </button>
                </div>
              </motion.form>
            ) : (
              <motion.form key="s2" initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 12 }} transition={{ duration: 0.2 }} onSubmit={submitKeyword} className="mt-6">
                <label className="text-[11px] font-semibold tracking-wide uppercase text-neutral-500 flex items-center gap-1.5">
                  <KeyRound className="w-3.5 h-3.5 text-[#d8651a]" /> {copy.kwTitle}
                </label>
                <SecretInput value={keyword} onChange={(e) => { setKeyword(e.target.value); setErr(''); }} placeholder="•••••"
                  testId="login-keyword-input" show={showKw} onToggle={() => setShowKw((v) => !v)} autoFocus />
                {err && (
                  <div className="mt-3" data-testid="login-error-box">
                    <p className="text-[12.5px] text-red-500" data-testid="login-error">{err}</p>
                    <p className="mt-1 text-[11.5px] text-neutral-400">{copy.rescue} <a href="mailto:support@kitxens.com" className="font-semibold text-[#F77F11] hover:underline" data-testid="login-support-link">support@kitxens.com</a></p>
                  </div>
                )}
                <button type="submit" disabled={busy} className="mt-5 w-full h-12 rounded-xl bg-[#F77F11] hover:bg-[#d8651a] disabled:opacity-60 text-white font-semibold text-sm transition-colors inline-flex items-center justify-center gap-2" data-testid="login-submit-btn">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />} {busy ? copy.submitting : copy.submit}
                </button>
                <button type="button" onClick={() => { setStep(1); setErr(''); }} className="mt-3 w-full text-[12.5px] text-neutral-500 hover:text-neutral-800 transition-colors" data-testid="login-back-btn">
                  {copy.back}
                </button>
              </motion.form>
            )}
          </AnimatePresence>
        </div>
        <RequestInvitation isEs={isEs} />
      </motion.div>
    </AuthShell>
  );
}
